import type { CurrentSession } from '@elite-residence/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useApiClient } from '../api/ApiClientProvider';
import { SESSION_KEY, useSignedOut } from '../auth/session';
import { type Dictionary, useText } from '../i18n/LocaleProvider';

// The signed-in Staff member's own account: password change (T-2 policy;
// rotates the session and signs out every other session) and signing out
// everywhere (V1-1-05).

const text: Dictionary<
  | 'title'
  | 'sessionEnds'
  | 'changePassword'
  | 'current'
  | 'next'
  | 'repeat'
  | 'rule'
  | 'save'
  | 'saved'
  | 'mismatch'
  | 'tooShort'
  | 'compromised'
  | 'wrongCurrent'
  | 'failed'
  | 'everywhere'
  | 'everywhereHint'
  | 'signOutAll'
> = {
  en: {
    title: 'My account',
    sessionEnds: 'This session ends at the latest at (UTC):',
    changePassword: 'Change password',
    current: 'Current password',
    next: 'New password',
    repeat: 'Repeat the new password',
    rule: 'At least 15 characters. A passphrase of several words works well.',
    save: 'Change password',
    saved: 'Your password was changed. Your other sessions were signed out.',
    mismatch: 'The two passwords do not match.',
    tooShort: 'The password must have at least 15 characters.',
    compromised: 'This password is known to be unsafe. Choose a different one.',
    wrongCurrent: 'The current password is not correct.',
    failed: 'The change could not be completed. Please try again.',
    everywhere: 'Sign out everywhere',
    everywhereHint: 'Ends every session of your account, including this one.',
    signOutAll: 'Sign out everywhere',
  },
  ar: {
    title: 'حسابي',
    sessionEnds: 'تنتهي هذه الجلسة في موعد أقصاه (UTC):',
    changePassword: 'تغيير كلمة المرور',
    current: 'كلمة المرور الحالية',
    next: 'كلمة المرور الجديدة',
    repeat: 'أعد كتابة كلمة المرور الجديدة',
    rule: '15 حرفًا على الأقل. عبارة من عدة كلمات خيار جيد.',
    save: 'تغيير كلمة المرور',
    saved: 'تم تغيير كلمة المرور وتسجيل الخروج من جلساتك الأخرى.',
    mismatch: 'كلمتا المرور غير متطابقتين.',
    tooShort: 'يجب أن تتكوّن كلمة المرور من 15 حرفًا على الأقل.',
    compromised: 'كلمة المرور هذه معروفة بأنها غير آمنة. اختر كلمة أخرى.',
    wrongCurrent: 'كلمة المرور الحالية غير صحيحة.',
    failed: 'تعذّر إتمام التغيير. يرجى المحاولة مرة أخرى.',
    everywhere: 'تسجيل الخروج من كل الأجهزة',
    everywhereHint: 'ينهي جميع جلسات حسابك، بما فيها هذه الجلسة.',
    signOutAll: 'تسجيل الخروج من كل الأجهزة',
  },
};

export function AccountPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const queries = useQueryClient();
  const signedOut = useSignedOut();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [message, setMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function change(event: FormEvent) {
    event.preventDefault();
    if (next !== repeat) return setMessage({ kind: 'error', text: t.mismatch });
    if ([...next.normalize('NFC')].length < 15) return setMessage({ kind: 'error', text: t.tooShort });
    setBusy(true);
    try {
      const response = await client.changePassword({ body: { currentPassword: current, newPassword: next } });
      if (response.status === 200) {
        setMessage({ kind: 'success', text: t.saved });
        setCurrent('');
        setNext('');
        setRepeat('');
        await queries.invalidateQueries({ queryKey: SESSION_KEY });
      } else if (response.status === 400) {
        const issues = (response.body as unknown as { error?: { issues?: { code: string }[] } }).error?.issues ?? [];
        setMessage({
          kind: 'error',
          text: issues.some((i) => i.code === 'password_compromised') ? t.compromised : t.tooShort,
        });
      } else if (response.status === 401) {
        setMessage({ kind: 'error', text: t.wrongCurrent });
      } else {
        setMessage({ kind: 'error', text: t.failed });
      }
    } catch {
      setMessage({ kind: 'error', text: t.failed });
    } finally {
      setBusy(false);
    }
  }

  async function signOutEverywhere() {
    await client.logout({ body: { all: true } }).catch(() => undefined);
    signedOut();
    // A full navigation discards every in-memory result of the signed-in
    // session (no stale business data survives sign-out).
    window.location.replace('/login');
  }

  return (
    <main>
      <h1>{t.title}</h1>
      <section className="er-card">
        <p>
          {t.sessionEnds} <span className="er-mono">{session.absoluteExpiresAt}</span>
        </p>
      </section>
      <section className="er-card" aria-labelledby="change-password">
        <h2 id="change-password">{t.changePassword}</h2>
        {message && (
          <p className={`er-alert ${message.kind}`} role={message.kind === 'error' ? 'alert' : 'status'}>
            {message.text}
          </p>
        )}
        <form onSubmit={change} noValidate>
          <div className="er-field">
            <label htmlFor="current-password">{t.current}</label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="er-field">
            <label htmlFor="next-password">{t.next}</label>
            <input
              id="next-password"
              type="password"
              autoComplete="new-password"
              aria-describedby="next-rule"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
            <span id="next-rule" className="er-hint">
              {t.rule}
            </span>
          </div>
          <div className="er-field">
            <label htmlFor="repeat-next">{t.repeat}</label>
            <input
              id="repeat-next"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          </div>
          <button className="er-button" type="submit" disabled={busy || !current || !next || !repeat}>
            {t.save}
          </button>
        </form>
      </section>
      <section className="er-card" aria-labelledby="everywhere">
        <h2 id="everywhere">{t.everywhere}</h2>
        <p className="er-hint">{t.everywhereHint}</p>
        <button className="er-button danger" type="button" onClick={() => void signOutEverywhere()}>
          {t.signOutAll}
        </button>
      </section>
    </main>
  );
}
